// HeaderNavigation.jsx

import React, { useContext, useState, useEffect } from "react";
import {
  Header,
  Box,
  Image,
  Avatar,
  DropButton,
  CheckBox,
  Text,
  Button,
  Layer,
  // Heading,
} from "grommet";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
// IMPORTANT: Make sure this is the correct import for your setup
import { jwtDecode } from "jwt-decode";

import API_URL from "../../config/config";
import { AuthContext } from "../../contexts/AuthContext";
import LogoutButton from "../LogoutButton";
import UpgradeToManager from "./UpgradeToManager";
// import VideoTutorialsGrid from "../../components/ManagerPortal/VideoTutorialsGrid";
import UserTourModal from "../UserTourModal";

export const HeaderNavigation = ({ quizStarted = false }) => {
  const { user, jwtToken } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [userInitials, setUserInitials] = useState("");
  const [userid, setUserId] = useState("");
  const [jwtData, setJwtData] = useState("");

  // Track whether we are in manager view (toggle = "on")
  const [isManagerView, setIsManagerView] = useState(false);

  // Store a "pending" new toggle value while we ask for confirmation
  const [pendingView, setPendingView] = useState(null);

  // For showing/hiding the confirmation Layer
  const [showConfirm, setShowConfirm] = useState(false);

  // Control the DropButton’s open state (avoid using 'open' because it’s a restricted global)
  const [dropOpen, setDropOpen] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [isManager, setIsManager] = useState(false); // Track manager status
  const [showModal, setShowModal] = useState(false);
  // const [actualUserId, setActualUserId] = useState(null);


  // useEffect(() => {
  //   // 1. grab token (adjust key if yours is different)
  //   if (!jwtToken) return;

  //   // 2. decode
  //   let decoded;
  //   try {
  //     decoded = jwtDecode(jwtToken);
  //   } catch {
  //     console.error('Invalid JWT');
  //     return;
  //   }

  //   // 3. pick correct ID based on role
  //   const { role, user_table_id, user_id } = decoded;
  //   const id =
  //     role === 'manager'
  //       ? user_table_id
  //       : role === 'user'
  //         ? user_id
  //         : null;

  //   setActualUserId(id);
  // }, [jwtToken]);

  const createTicketAndEmail = async (
    userId,
    type,
    subjectTemplate,
    bodyTemplate
  ) => {
    try {
      // 1. Create ticket
      const response = await fetch('/api/ticket/generate-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, type })
      });

      if (!response.ok) {
        throw new Error(`Status ${response.status}`);
      }

      const { ticket_number } = await response.json();

      // 2. Build subject & body
      const subject = encodeURIComponent(
        `${subjectTemplate} [Ticket: ${ticket_number}]`
      );
      const body = encodeURIComponent(
        `${bodyTemplate}\n\nTicket Number: ${ticket_number}`
      );
      const bcc = encodeURIComponent('hpe.skilladvisor@meteoros.in');

      // 3. Open mailto
      const mailtoURL = `mailto:hpeskill.advisor@hpe.com` +
        `?subject=${subject}` +
        `&bcc=${bcc}` +
        `&body=${body}`;

      window.open(mailtoURL);
    } catch (err) {
      console.error('Unable to generate ticket', err);
      alert('Could not create ticket. Please try again.');
    }
  };


  /**
   * 1) Determine whether the path includes "manager"
   *    -> if yes, we set isManagerView to true
   */
  useEffect(() => {
    if (location.pathname.toLowerCase().includes("manager")) {
      setIsManagerView(true);
    } else {
      setIsManagerView(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (jwtToken) {
      try {
        const decodedToken = jwtDecode(jwtToken);
        setJwtData(decodedToken);

        // console.log("Decoded JWT Data:", decodedToken);

        const userId =
          decodedToken.role === "manager"
            ? decodedToken.user_table_id
            : decodedToken.user_id;

        setUserId(userId);
        setIsManager(decodedToken.role === "manager"); // Set manager status

        const fetchUserName = async () => {
          const response = await axios.get(
            `${API_URL}/user/getUserNameById/${userId}`,
          );
          // console.log("User name response:", response.data);
          const name = response.data.name;
          const initials = name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();
          setUserInitials(initials);
        };

        if (userId) {
          fetchUserName();
        }
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, [jwtToken]);

  useEffect(() => {
    const checkInviteStatus = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/manager/check_invite_status/${userid}`,
        );
        setInviteStatus(response.data.inviteStatus); // Expecting true/false from API
      } catch (error) {
        console.error("Error fetching invite status:", error);
        setInviteStatus(false); // Default to false if error occurs
      }
    };

    checkInviteStatus();
  }, [userid]);

  if (inviteStatus === null) {
    return null; // Prevents rendering until API response is received
  }

  /**
   * 3) Toggle switch handler -> ask for confirmation
   */
  const handleToggleView = (event) => {
    const newChecked = event.target.checked;
    setPendingView(newChecked);
    setShowConfirm(true);
  };

  /**
   * 4) If user confirms, navigate to the new route
   */
  const handleConfirm = () => {
    if (pendingView == null) return; // no new value

    setIsManagerView(pendingView);
    setShowConfirm(false);

    if (pendingView) {
      // Manager view
      navigate(`/${user?.user_id}/manager/dashboard`);
    } else {
      // User view
      navigate(`/add-proficiency/${user?.user_table_id}`);
    }

    setPendingView(null);
  };

  /**
   * 5) If user cancels, revert the toggle
   */
  const handleCancel = () => {
    setShowConfirm(false);
    setPendingView(null);
  };

  /**
   * Determine which message to show in the confirmation dialog
   */
  const getConfirmationMessage = () => {
    if (pendingView === null) return "";
    return pendingView
      ? "You are switching to Manager View. Are you sure?"
      : "You are switching to User View. Are you sure?";
  };

  const handleUpgrade = () => {
    setIsManager(true); // Update manager status after successful upgrade
  };

  const dropdownContent = (
    <Box pad="small" direction="column" gap="small">
      <Box align="center" justify="center">
        {/* Hide Home button when Manager portal is on */}
        {!isManagerView && (
          <Button
            label="Home"
            onClick={() =>
              navigate(
                // isManager
                //   ? `/${user?.user_id}/manager/dashboard`
                //   : `/add-proficiency/${userid}`
                `/add-proficiency/${userid}`,
              )
            }
          />
        )}
        <Button
          label="Dashboard"
          onClick={() =>
            navigate(
              isManagerView
                ? `/${user?.user_id}/manager/dashboard`
                : `/dashboard/user/${userid}`,
            )
          }
        />
        <Button
          label="Self guided help"
          onClick={() =>
            navigate(isManagerView ? `/manager/contact` : `/contact`)
          }
        />
        {!isManagerView &&
          jwtData.role !== "manager" &&
          (inviteStatus ? (
            // <Button
            //   label="Upgrade to Manager"
            //   onClick={() => {
            //     const subject = encodeURIComponent(
            //       "Request for Manager Role Upgrade",
            //     );
            //     const body = encodeURIComponent(
            //       `Hello Team,\n\nI would like to request an upgrade to a Manager role.\n\nThank you.`,
            //     );
            //     window.open(
            //       `mailto: hpe.skilladvisor@meteoros.in?subject=${subject}&body=${body}`,
            //     );
            //   }}
            //   color="white"
            // />
            <Button
              label="Upgrade to Manager"
              onClick={() =>
                createTicketAndEmail(
                  userid,
                  'Manager Role Upgrade',
                  'Request for Manager Role Upgrade',
                  'Hello Team,\n\nI would like to request an upgrade to a Manager role.\n\nThank you.'
                )
              }
              color="white"
            />

          ) : (
            <UpgradeToManager user_id={userid} onUpgrade={handleUpgrade} />
          ))}
      </Box>
      <Box
        border={{ color: "brand", size: "small" }}
      // margin={{ vertical: "small" }}
      />
      <Box align="center" justify="center">
        <LogoutButton />
      </Box>
    </Box>
  );
  return (
    <Header
      fill="horizontal"
      background="black"
      pad={{ vertical: "small", horizontal: "large" }}
      height="70px"
      style={{ position: "relative" }} // Ensure relative positioning for absolute elements
    >
      {/* LEFT: Logo */}
      <Box
        direction="row"
        align="center"
        gap="small"
      // onClick={() => navigate(`/dashboard/${userid}`)}
      >
        <a
          href="https://education.hpe.com/ww/en/training/index.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image
            src="/hpe-logo.png"
            alt="App Logo"
            width="130px"
            height="55px"
          />
        </a>
      </Box>

      {/* ABSOLUTE POSITIONED RIGHT BOX */}
      {!quizStarted && ( // Only render if quizStarted is false
        <>
          {jwtToken && jwtData.role === "manager" && (
            <Box
              direction="row"
              align="center"
              gap="xsmall"
              justify="center"
              flex
              alignSelf="center"
              alignContent="center"
              margin={{ right: "130px" }}
            >
              <Text
                weight={!isManagerView ? "bold" : undefined}
                color={!isManagerView ? "accent-1" : "light-1"}
              >
                User portal
              </Text>

              <Box basis="60px" align="center">
                <CheckBox
                  toggle
                  checked={isManagerView}
                  onChange={handleToggleView}
                  align="center"
                />
              </Box>

              <Text
                weight={isManagerView ? "bold" : undefined}
                color={isManagerView ? "accent-1" : "light-1"}
              >
                Manager portal
              </Text>
            </Box>
          )}

          <Box
            align="end"
            direction="row"
            gap="small"
            style={{
              position: "absolute",
              right: "40px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {/* {isManagerView && (
                  <Button
                    label="Video Tutorials"
                    onClick={() => setShowVideoModal(true)}
                    alignSelf="center"
                  />
                )} */}

            {/* Avatar + DropButton */}
            {jwtToken && (
              <Box direction="row" width="auto" gap="small" align="center">
                {!isManagerView && !quizStarted && (
                  <Text
                    onClick={() => setShowModal(true)}
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    Quick start Guide
                  </Text>
                )}
                <DropButton
                  dropAlign={{ top: "bottom", right: "right" }}
                  dropContent={dropdownContent}
                  open={dropOpen}
                  onOpen={() => setDropOpen(true)}
                  onClose={() => setDropOpen(false)}
                >
                  <Avatar background="brand" size="medium">
                    {userInitials || "?"}
                  </Avatar>
                </DropButton>
              </Box>
            )}
          </Box>
          {showModal && (
            <UserTourModal
              showModal={showModal}
              setShowModal={setShowModal}
              userid={userid}
            />
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <Layer
          onEsc={handleCancel}
          onClickOutside={handleCancel}
          responsive={false}
        >
          <Box pad="medium" gap="small" width="medium">
            <Text>{getConfirmationMessage()}</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Confirm" primary onClick={handleConfirm} />
              <Button label="Cancel" onClick={handleCancel} />
            </Box>
          </Box>
        </Layer>
      )}

      {/* {showVideoModal && (
          <Layer
            onEsc={() => setShowVideoModal(false)}
            onClickOutside={() => setShowVideoModal(false)}
            margin="small"
            background="white"
          >
            <Box pad="medium" width="large" round="small">
              <Heading level={3} margin="none" textAlign="center" alignSelf="center" align="center" style={{color: "black"}}> 
                Watch Our Tutorials
              </Heading>
              <Text margin={{ bottom: "small" }} textAlign="center">
                Explore our video tutorials to get the most out of Skill Advisor.
              </Text>
              <Box width="xlarge" pad="medium" background="light-1" round="small">
                <Box align="center">
                  <VideoTutorialsGrid />
                </Box>
              </Box>
              <Box align="center" margin={{ top: "medium" }}>
                <Button label="Close" style={{color: "black"}} onClick={() => setShowVideoModal(false)} />
              </Box>
            </Box>
          </Layer>
        )} */}
    </Header>
  );
};
